/**
 * クエリの読み込み中・エラー表示の共通部品（audit C10）。
 * 取得エラーが「0件」や「読み込み中…」のまま固まって見えるのを防ぐ。
 */

import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { AlertTriangle, Loader2, RotateCw, X } from 'lucide-react'
import { errorMessage, type LintIssue } from '../lib/api'

export function QueryState<T>({ query, label = 'データ', loadingText = '読み込み中…', children }: {
  query: UseQueryResult<T>
  /** エラー文に入れる対象名（例: 承認キュー） */
  label?: string
  loadingText?: string
  children: (data: T) => ReactNode
}) {
  if (query.data === undefined) {
    if (query.isError) {
      return (
        <ErrorBox
          message={`${label}の読み込みに失敗しました: ${errorMessage(query.error)}`}
          onRetry={() => { void query.refetch() }}
        />
      )
    }
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 size={16} className="text-primary motion-safe:animate-spin" aria-hidden /> {loadingText}
      </p>
    )
  }
  return (
    <>
      {/* 再取得だけ失敗した場合は前回の内容を出しつつ知らせる */}
      {query.isError && (
        <p role="status" className="mb-3 rounded-ctl bg-warn-bg px-3 py-2 text-sm text-warn">
          最新の{label}を取得できませんでした（表示は前回の内容です）: {errorMessage(query.error)}
        </p>
      )}
      {children(query.data)}
    </>
  )
}

/** 取得失敗の表示（再試行ボタン付き） */
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-ctl bg-bad-bg px-3 py-2 text-sm text-bad">
      <AlertTriangle size={16} className="shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="gm-btn gm-btn-secondary gm-btn-sm">
          <RotateCw size={13} aria-hidden /> 再読み込み
        </button>
      )}
    </div>
  )
}

/** 操作（mutation）の失敗をその場に出す標準表示。lint の不合格理由があれば箇条書きで添える */
export function InlineError({ message, issues, className = '' }: {
  message: string | null | undefined
  issues?: LintIssue[]
  className?: string
}) {
  if (!message) return null
  return (
    <div role="alert" className={`text-sm text-bad ${className}`}>
      <p>{message}</p>
      {!!issues?.length && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {issues.map((i, idx) => (
            <li key={`${i.code}-${idx}`}>
              {i.message}
              {i.level === 'warn' && <span className="ml-1 text-xs text-warn">（注意）</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** 成功・失敗のお知らせ（閉じるボタン付き） */
export function Notice({ kind, children, onClose }: {
  kind: 'success' | 'error' | 'info'
  children: ReactNode
  onClose?: () => void
}) {
  const cls = {
    success: 'bg-ok-bg text-ok',
    error: 'bg-bad-bg text-bad',
    info: 'bg-primary-subtle text-primary',
  }[kind]
  return (
    <div role={kind === 'error' ? 'alert' : 'status'} className={`flex items-start gap-3 rounded-ctl px-3 py-2 text-sm ${cls}`}>
      <div className="min-w-0 flex-1">{children}</div>
      {onClose && (
        <button onClick={onClose} aria-label="閉じる" className="shrink-0 rounded-ctl p-0.5 opacity-70 hover:opacity-100">
          <X size={16} aria-hidden />
        </button>
      )}
    </div>
  )
}
